/**
 * ============================================================================
 *  useVoiceShortcut — 全局语音输入快捷键
 * ============================================================================
 *
 * ──────────────────────────────────────────────────────────────────────────
 * 📌 职责
 * ──────────────────────────────────────────────────────────────────────────
 *  提供一套与 VoiceMicButton 完全相同的录音能力，但以键盘快捷键触发。
 *  识别结果根据触发场景的不同，有两条分支：
 *    路径 A — textarea 有光标：本 Hook 直接录音并通过 setNativeValue 插入文本
 *    路径 B — 无 textarea 焦点：广播 VOICE_SHORTCUT_DOWN/UP，由各节点自行处理
 *             （典型场景：文字节点仅被选中，未双击进入编辑态）
 *
 * ──────────────────────────────────────────────────────────────────────────
 * 🔄 工作流程
 * ──────────────────────────────────────────────────────────────────────────
 *
 *  配置阶段：
 *    用户打开左下角「?」快捷键面板 → 点击「语音输入」白色区域
 *    → 进入 isListening 模式 → 按下任意组合键（如 ⌘+M）
 *    → JSON 序列化后存入 localStorage（key: 'voice_shortcut'）
 *    → 广播 VOICE_SHORTCUT_CHANGED 事件给所有监听者
 *
 *  使用阶段（路径 A — textarea 有光标）：
 *    1. 光标位于带有 data-voice-shortcut="true" 的 textarea / input
 *    2. 按住配置好的快捷键（keydown，捕获阶段）
 *    3. 广播 VOICE_SHORTCUT_DOWN { textareaHandled: true }
 *    4. 读取 textarea 光标位置，拆分 beforeCursor + afterCursor
 *    5. 调用 useVoiceInput.startRecording() 开始录音
 *    6. ASR 识别文本通过 setNativeValue() 插入到光标位置
 *       （setNativeValue 绕过 React 的 value 控制，触发 input 事件让 React 感知到更新）
 *    7. 松开快捷键（keyup）→ 广播 VOICE_SHORTCUT_UP → 500ms 后 stopRecording()
 *    8. 广播 VOICE_RECORDING_STATE → 当前节点的 VoiceMicButton 同步录音动画
 *       ⚠️ VoiceMicButton 精确匹配 document.activeElement === textareaRef.current
 *          只有焦点节点的麦克风亮，其他节点不受影响
 *
 *  使用阶段（路径 B — 无 textarea 焦点，如文字节点仅选中）：
 *    1. 按住快捷键 → 广播 VOICE_SHORTCUT_DOWN { textareaHandled: false }
 *    2. TextNodeView 中的 _VoiceShortcutForSelectedNode 监听此事件
 *       → 通过 triggerRef 调用工具栏 VoiceMicButton 的 start()
 *    3. 松开快捷键 → 广播 VOICE_SHORTCUT_UP
 *       → _VoiceShortcutForSelectedNode 调用 VoiceMicButton 的 stop()
 *
 * ──────────────────────────────────────────────────────────────────────────
 * 📦 导出 API
 * ──────────────────────────────────────────────────────────────────────────
 *
 *   Hook（EditorPage 中调用一次即可）:
 *     useVoiceShortcut()  →  { isVoiceShortcutActive: boolean }
 *
 *   工具函数（供 BottomControls 使用）:
 *     getVoiceShortcut()                — 读取 localStorage 快捷键配置
 *     setVoiceShortcut(shortcut | null) — 写入（传 null 清除）
 *     formatShortcut(shortcut)          — 格式化为 "⌘ + M" 等人类可读字符串
 *
 *   事件名（用于跨组件广播）:
 *     VOICE_SHORTCUT_CHANGED  — 快捷键配置变化时广播
 *     VOICE_RECORDING_STATE   — 录音状态变化时广播（detail: { active: boolean }）
 *                               VoiceMicButton 监听此事件同步红色脉冲动画
 *     VOICE_SHORTCUT_DOWN     — 快捷键按下时广播（detail: { textareaHandled: boolean }）
 *                               textareaHandled=true 表示路径 A 已处理，路径 B 应跳过
 *     VOICE_SHORTCUT_UP       — 快捷键松开时广播，路径 B 组件据此停止录音
 *
 * ──────────────────────────────────────────────────────────────────────────
 * ⚠️ 关键约束
 * ──────────────────────────────────────────────────────────────────────────
 *
 *  data-voice-shortcut="true" — textarea 白名单标记
 *    只有带此属性的 textarea / input 才属于路径 A 处理范围。
 *    已标记：NodeConsole 内联/全屏 textarea、TextNodeView 内联/全屏 textarea
 *    未标记的 textarea（如搜索框、设置输入框）不触发语音快捷键。
 *
 *  event.code vs event.key
 *    macOS 上 Option+Z → event.key = "Ω"（特殊字符），event.code = "KeyZ"（物理键位）。
 *    快捷键存储和匹配均使用 code，彻底规避 Option 键的字符映射问题。
 *    旧版只存了 key 的配置仍可兼容（fallback 到 key 比较）。
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import useVoiceInput from './useVoiceInput';

// ── 常量 ──
const RELEASE_DELAY_MS = 500;
const STORAGE_KEY = 'voice_shortcut';
export const VOICE_SHORTCUT_CHANGED = 'voice-shortcut-changed';
/** 全局录音状态事件 —— VoiceMicButton 监听此事件同步显示录音动画 */
export const VOICE_RECORDING_STATE = 'voice-recording-state';
/** 快捷键按下/松开广播 —— 供无 textarea 聚焦场景（如文字节点选中态）自行处理 */
export const VOICE_SHORTCUT_DOWN = 'voice-shortcut-down';
export const VOICE_SHORTCUT_UP = 'voice-shortcut-up';
export const VOICE_TRANSCRIPT_RECEIVED = 'voice-transcript-received';

// ── 原生 value setter（用于更新 React 受控 textarea/input 的值） ──
const nativeTextAreaSetter = Object.getOwnPropertyDescriptor(
  window.HTMLTextAreaElement.prototype, 'value'
)?.set;
const nativeInputSetter = Object.getOwnPropertyDescriptor(
  window.HTMLInputElement.prototype, 'value'
)?.set;

/** 通过原生 setter 设置值并触发 React 的 onChange */
function setNativeValue(element, value) {
  const setter = element.tagName === 'TEXTAREA' ? nativeTextAreaSetter : nativeInputSetter;
  if (!setter) return;
  setter.call(element, value);
  element.dispatchEvent(new Event('input', { bubbles: true }));
}

// ── 快捷键匹配 ──
// 优先用 code（物理键位），不受 macOS Option 键字符映射影响
// 旧版只存了 key 的配置仍可兼容（fallback 到 key 比较）
function matchesShortcut(event, shortcut) {
  if (!shortcut) return false;
  const keyMatch = shortcut.code
    ? event.code === shortcut.code
    : event.key.toLowerCase() === (shortcut.key || '').toLowerCase();
  return (
    keyMatch &&
    !!event.ctrlKey === !!shortcut.ctrlKey &&
    !!event.metaKey === !!shortcut.metaKey &&
    !!event.altKey === !!shortcut.altKey &&
    !!event.shiftKey === !!shortcut.shiftKey
  );
}

/** 判断 keyup 的 key 是否是快捷键的主键 */
function isMainKeyUp(event, shortcut) {
  if (!shortcut) return false;
  return shortcut.code
    ? event.code === shortcut.code
    : event.key.toLowerCase() === (shortcut.key || '').toLowerCase();
}

// ── 公开工具函数 ──

/** 读取已保存的快捷键配置 */
export function getVoiceShortcut() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

/** 保存快捷键配置（传 null 清除） */
export function setVoiceShortcut(shortcut) {
  if (shortcut) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(shortcut));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
  window.dispatchEvent(new Event(VOICE_SHORTCUT_CHANGED));
}

/** 将 code 字符串转换为可显示的键名，如 KeyZ→Z, Digit1→1 */
function codeToDisplay(code) {
  if (!code) return '';
  if (code.startsWith('Key')) return code.slice(3);    // KeyZ → Z
  if (code.startsWith('Digit')) return code.slice(5);  // Digit1 → 1
  const map = {
    Space: 'Space', Enter: '↩', Backspace: '⌫', Tab: 'Tab', Escape: 'Esc',
    ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→',
  };
  return map[code] || code;
}

/** 格式化快捷键为可读字符串，如 "⌘ + M" 或 "Ctrl + M" */
export function formatShortcut(shortcut) {
  if (!shortcut) return '';
  const isMac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');
  const parts = [];
  if (shortcut.ctrlKey) parts.push(isMac ? '⌃' : 'Ctrl');
  if (shortcut.altKey) parts.push(isMac ? '⌥' : 'Alt');
  if (shortcut.shiftKey) parts.push(isMac ? '⇧' : 'Shift');
  if (shortcut.metaKey) parts.push(isMac ? '⌘' : 'Win');
  // 优先用 code 派生可读键名，避免 Option 产生的 Ω/å 等特殊字符显示
  const displayKey = shortcut.code
    ? codeToDisplay(shortcut.code)
    : (shortcut.key?.length === 1 ? shortcut.key.toUpperCase() : shortcut.key || '');
  parts.push(displayKey);
  return parts.join(' + ');
}

// ── Hook 本体 ──

/**
 * 全局语音快捷键 Hook
 * 在 EditorPage 中调用一次即可，无需传参。
 *
 * @returns {{ isVoiceShortcutActive: boolean }}
 */
export default function useVoiceShortcut() {
  const [isActive, setIsActive] = useState(false);

  // 录音目标：按下快捷键时捕获的 textarea/input DOM
  const targetRef = useRef(null);
  const beforeCursorRef = useRef('');
  const afterCursorRef = useRef('');
  const pressActiveRef = useRef(false);
  const releaseTimerRef = useRef(null);
  const shortcutRef = useRef(getVoiceShortcut());

  // 监听快捷键配置变化
  useEffect(() => {
    const sync = () => { shortcutRef.current = getVoiceShortcut(); };
    window.addEventListener(VOICE_SHORTCUT_CHANGED, sync);
    return () => window.removeEventListener(VOICE_SHORTCUT_CHANGED, sync);
  }, []);

  // ASR 回调：将识别文本插入到光标位置
  const handleTranscript = useCallback((text) => {
    if (!text || !targetRef.current) return;
    window.dispatchEvent(new Event(VOICE_TRANSCRIPT_RECEIVED));
    const newValue = beforeCursorRef.current + text + afterCursorRef.current;
    setNativeValue(targetRef.current, newValue);
    // 把光标移到插入文本之后
    const cursorPos = beforeCursorRef.current.length + text.length;
    try {
      targetRef.current.setSelectionRange(cursorPos, cursorPos);
    } catch { /* 某些 input type 不支持 */ }
  }, []);

  const handleError = useCallback((msg) => {
    console.warn('🎤 [VoiceShortcut] 错误:', msg);
  }, []);

  const { isRecording, startRecording, stopRecording } = useVoiceInput({
    onTranscript: handleTranscript,
    onError: handleError,
  });

  // 同步 isActive 状态，并广播给所有 VoiceMicButton
  useEffect(() => {
    setIsActive(isRecording);
    window.dispatchEvent(new CustomEvent(VOICE_RECORDING_STATE, { detail: { active: isRecording } }));
  }, [isRecording]);

  const clearReleaseTimer = useCallback(() => {
    if (releaseTimerRef.current) {
      clearTimeout(releaseTimerRef.current);
      releaseTimerRef.current = null;
    }
  }, []);

  // ── 全局 keydown / keyup 监听 ──
  useEffect(() => {
    const handleKeyDown = (e) => {
      const sc = shortcutRef.current;
      if (!sc || !matchesShortcut(e, sc)) return;
      if (pressActiveRef.current) {
        e.preventDefault(); // 阻止按住时的重复触发
        return;
      }

      // 检查当前是否有 textarea/input 聚焦并标记为语音快捷键目标
      const el = document.activeElement;
      const taHandled = !!(el &&
        (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') &&
        el.dataset.voiceShortcut === 'true');

      // 广播按下事件，告知其他组件（如文字节点选中态）是否需要自行处理
      window.dispatchEvent(new CustomEvent(VOICE_SHORTCUT_DOWN, { detail: { textareaHandled: taHandled } }));

      if (!taHandled) return; // 没有聚焦的 textarea → 外部组件自己处理，此处退出

      e.preventDefault();
      pressActiveRef.current = true;
      clearReleaseTimer();

      // 捕获光标位置
      targetRef.current = el;
      const value = el.value || '';
      const start = typeof el.selectionStart === 'number' ? el.selectionStart : value.length;
      const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : value.length;
      beforeCursorRef.current = value.slice(0, start);
      afterCursorRef.current = value.slice(end);

      startRecording();
    };

    const handleKeyUp = (e) => {
      const sc = shortcutRef.current;
      if (!sc || !isMainKeyUp(e, sc)) return;

      // 广播松开事件，供文字节点等外部组件响应
      window.dispatchEvent(new CustomEvent(VOICE_SHORTCUT_UP));

      if (!pressActiveRef.current) return;
      pressActiveRef.current = false;
      clearReleaseTimer();
      releaseTimerRef.current = setTimeout(() => {
        stopRecording();
        releaseTimerRef.current = null;
      }, RELEASE_DELAY_MS);
    };

    window.addEventListener('keydown', handleKeyDown, true); // capture phase
    window.addEventListener('keyup', handleKeyUp, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
      clearReleaseTimer();
    };
  }, [startRecording, stopRecording, clearReleaseTimer]);

  return { isVoiceShortcutActive: isActive };
}
